
import { EntityKind } from "../net/connection";
import { CharacterShadowMap } from "./charactershadow";
import type { CollisionMap, GuildAttributeTable } from "../sim/collision";
import { Atmosphere, fogFor, slopeFloorFor } from "./atmosphere";
import { Camera, FOG_FAR_MM, FOG_NEAR_MM, type Fog, type TreeCollisionData } from "./camera";
import type { Mat4 } from "./view";
import { ObjectSurface } from "./objectsurface";
import type { EffectRenderer } from "./effects";
import { EntityPainter, GUILD_SYMBOL_RACE, type RenderFrame, type RenderedCharacterParts } from "./entities";
import type { ShadeRaster } from "./shade";
import { setCreatureLight, type ClipEntry, type ClipName } from "./creaturemesh";
import type { PlacedSphere } from "./collisionvolumes";
import { GlError, createContext } from "./gl/context";
import {
  GlBuildings,
  GlDropItems,
  GlGuildSymbols,
  loadGuildSymbolModel,
  type PlacedInsignia,
  GlTrees,
  loadDropModels,
  type LyingItem,
  createPropTexture,
  type PcBlockerFrame,
  type PropAssets,
} from "./gl/props";
import {
  createCoverageTexture,
  createFlatNormalTexture,
  createFullyLitTexture,
  createHeightTexture,
  createNormalTexture,
  createTextureArray,
  loadImage,
} from "./gl/texture";
import type { CreaturePalette } from "./creature";
import type { HeightField } from "./heightfield";
import type { MapEnvironment } from "./mapassets";
import type { RenderBackend, SceneRenderer } from "./scene";
import { GlTerrain, toHeightSource } from "./terrain";
import type { TileMap } from "./tiles";
import { LensFlare, type FlareView } from "./lensflare";
import { GlWater } from "./gl/water";
import type { RgbaImage } from "./image";
import type { WaterMap } from "./watermap";
import { SkyBox, loadCloudTexture } from "./skybox";
import { SnowField, type SnowBasis } from "./snowfield";
import { GlSnow } from "./gl/snow";
import { Renderer } from "./renderer";

const VOID_COLOUR: readonly [number, number, number] = [0.690196, 0.741176, 0.839216];

/** A 3x panel costs nine times the fill for a difference no one sees on ground
 *  textures. The same cap the Canvas2D renderer uses, for the same reason. */
const MAX_DEVICE_PIXEL_RATIO = 2;


/** `performance.now()` where there is one; a vitest stub canvas has none. */
function now(): number {
  return typeof performance === "undefined" ? 0 : performance.now();
}

export class GlRenderer implements SceneRenderer {
  readonly backend: RenderBackend = "webgl2";
  readonly camera = new Camera();

  private readonly gl: WebGL2RenderingContext;
  private readonly glCanvas: HTMLCanvasElement;
  private readonly overlay: CanvasRenderingContext2D;
  private readonly painter: EntityPainter;
  /** `m_lpCharacterShadowMapTexture`, or null where the context cannot hold one. */
  private readonly characterShadow: CharacterShadowMap | null;
  private readonly terrain: GlTerrain;
  private readonly atmosphere: Atmosphere;
  /** The map's own sky. Null when the shaders would not compile, for the same
   *  reason `buildings` is: losing the gradient costs the sky, not the frame --
   *  and what is behind the ground then is the cleared fog colour, exactly what
   *  it was before this class drew one. */
  private readonly sky: SkyBox | null;
  /** The sun's flare, null for the same reason `sky` is. Draws nothing at all
   *  until a record enables it, which none of the 42 shipped ones does. */
  private readonly flare: LensFlare | null;
  private readonly tiles: TileMap;
  /** Null when the manifests failed to fetch, a texture failed to decode, or
   *  the props shaders failed to compile -- any of which costs the skyline,
   *  not the renderer: terrain and entities keep working exactly as they did
   *  before buildings existed. See the constructor's own try/catch. */
  private readonly buildings: GlBuildings | null;
  private readonly trees: GlTrees | null;
  /** The ground items' drop models; null until the pack loads, and for good
   *  when it does not, which leaves every ground item its glyph. */
  private dropItems: GlDropItems | null = null;
  private guildAttributes: GuildAttributeTable | null = null;
  /** The standing guild buildings the height field's building surfaces were
   *  last built from, so they are rebuilt only when one comes or goes. */
  private buildingSurfaceKey = "";
  /** Race 14200's own model; null until its pack loads, and for good when it
   *  does not, which leaves each insignia its schematic. */
  private guildSymbols: GlGuildSymbols | null = null;
  /** Each guild's served symbol, uploaded once per image it is served as. */
  private readonly symbolTextures = new Map<number, { readonly image: CanvasImageSource; readonly texture: WebGLTexture }>();
  private dpr = 1;
  private pixelRatioCap = MAX_DEVICE_PIXEL_RATIO;
  /** `CSnowEnvironment`'s flakes, simulated whether or not the pass that
   *  draws them has its texture yet. */
  private readonly snowField = new SnowField(Math.random);
  private snowPass: GlSnow | null = null;
  private snowTexturePending = false;
  private snowing = false;
  private lastSnowMs: number | null = null;
  /** The map's water surface, null on a map with none or where its shaders
   *  would not compile -- the ground is still drawn, only the lakes are not. */
  private water: GlWater | null = null;

  /** `?draw=lite`: draw the actors, skip the world behind them. */
  private reduced = false;

  readonly profile = {
    frames: 0,
    setupMs: 0,
    terrainMs: 0,
    propsMs: 0,
    atmosphereMs: 0,
    actorsMs: 0,
    snowMs: 0,
  };
  private contextLost = false;
  private fallbackRenderer: Renderer | null = null;
  private terrainMap: CollisionMap | null = null;
  private palettes: Readonly<Record<string, CreaturePalette>> = {};
  private night = false;
  /** Null until `setEnvironment`: a renderer built by a test that never calls
   *  it keeps the fallback fade rather than inventing a record. */
  private environment: MapEnvironment | null = null;
  environmentReady: Promise<void> = Promise.resolve();
  private weatherKind = 0;

  constructor(
    private readonly overlayCanvas: HTMLCanvasElement,
    atlas: ImageBitmap | HTMLImageElement,
    tiles: TileMap,
    private readonly heightField: HeightField,
    shadow: ImageBitmap | HTMLImageElement | null = null,
    normal: ImageBitmap | HTMLImageElement | null = null,
    props: PropAssets | null = null,
  ) {
    this.tiles = tiles;
    this.glCanvas = document.createElement("canvas");
    this.gl = createContext(this.glCanvas);

    try {
      this.terrain = new GlTerrain(
        this.gl,
        createTextureArray(this.gl, atlas, tiles.layers),
        shadow === null
          ? createFullyLitTexture(this.gl)
          : createCoverageTexture(this.gl, shadow),
        normal === null ? createFlatNormalTexture(this.gl) : createNormalTexture(this.gl, normal),
        toHeightSource(createHeightTexture(this.gl, heightField.texture), heightField.texture),
      );
      this.atmosphere = new Atmosphere(this.gl);
    } catch (error) {
      // Hand the context back rather than leaving it live on a canvas that is
      // about to be dropped: the fallback renderer is about to want the GPU too,
      // and browsers cap how many contexts a page may hold.
      this.gl.getExtension("WEBGL_lose_context")?.loseContext();
      throw error;
    }

    // Outside the try above: a sky that will not compile must not cost the
    // terrain, and the two failures are independent.
    let sky: SkyBox | null = null;
    try {
      sky = new SkyBox(this.gl);
    } catch {
      sky = null;
    }
    this.sky = sky;

    let flare: LensFlare | null = null;
    try {
      flare = new LensFlare(this.gl);
    } catch {
      flare = null;
    }
    this.flare = flare;

    const built = props === null ? null : buildProps(this.gl, props);
    this.buildings = built?.buildings ?? null;
    this.trees = built?.trees ?? null;
    loadDropModels()
      .then(({ index, blob, images }) => {
        if (this.contextLost) return;
        const textures = new Map<string, WebGLTexture>();
        for (const [key, image] of images) textures.set(key, createPropTexture(this.gl, image));
        this.dropItems = new GlDropItems(this.gl, blob, index, textures);
      })
      .catch((error: unknown) => {
        console.warn(`drop models unavailable (${String(error)}); ground items keep their glyphs`);
      });
    loadGuildSymbolModel()
      .then(({ index, blob, images }) => {
        if (this.contextLost) return;
        const textures = new Map<string, WebGLTexture>();
        for (const [key, image] of images) textures.set(key, createPropTexture(this.gl, image));
        this.guildSymbols = new GlGuildSymbols(this.gl, blob, index, textures);
      })
      .catch((error: unknown) => {
        console.warn(`guild insignia model unavailable (${String(error)}); insignias keep their schematic`);
      });

    const overlay = overlayCanvas.getContext("2d", { alpha: true });
    if (overlay === null) throw new GlError("the overlay canvas refused a 2D context");
    this.overlay = overlay;
    this.painter = new EntityPainter(overlay, this.camera, heightField, this.gl, this.glCanvas);
    let characterShadow: CharacterShadowMap | null = null;
    try {
      characterShadow = new CharacterShadowMap(this.gl);
    } catch {
      characterShadow = null;
    }
    this.characterShadow = characterShadow;
    this.painter.setCharacterShadow(characterShadow);
    const mapSize = props?.placements.meta?.mapSize;
    if (props !== null && mapSize !== undefined) {
      this.painter.effects.setPlacedEffects(props.placements.effects ?? [], mapSize.patchesX, mapSize.patchesY);
    }

    stack(this.glCanvas);
    stack(overlayCanvas);
    overlayCanvas.parentNode?.insertBefore(this.glCanvas, overlayCanvas);
    // A lost context is not a crash: the page keeps running and every draw is a
    // no-op, which is the black screen this whole file is written against. Say so.
    this.glCanvas.addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      this.contextLost = true;
      this.activateFallback();
      console.error("WebGL context lost; switched to the Canvas2D renderer");
    });

    this.resize();
  }

  /** The collision grid: walkability for the shading, and the geometry both it
   *  and the tile indices are addressed by. */
  /** The map's shade raster every character's material colour is sampled
   *  from, or null to light every character in full colour. */
  setShade(raster: ShadeRaster | null): void {
    this.painter.setShade(raster);
  }

  setPalettes(palettes: Readonly<Record<string, CreaturePalette>>): void {
    this.palettes = palettes;
    this.painter.setPalettes(palettes);
    this.fallbackRenderer?.setPalettes(palettes);
  }

  setTerrain(map: CollisionMap): void {
    this.terrainMap = map;
    if (map.worldCellsX !== this.tiles.width || map.worldCellsY !== this.tiles.height) {
      // Not fatal -- the shading would just be wrong -- but silently wrong, which
      // is worse than loud and wrong.
      console.warn(
        `terrain grids disagree: collision is ${map.worldCellsX}x${map.worldCellsY}, ` +
          `tiles are ${this.tiles.width}x${this.tiles.height}`,
      );
    }
    if (!this.contextLost) {
      this.terrain.setSource({ grid: map, tiles: this.tiles, attributes: map });
    }
    this.fallbackRenderer?.setTerrain(map);
  }

  groundAtMm(xMm: number, yMm: number): number {
    return this.heightField.at(xMm, yMm);
  }

  /** Stand a map's baked water on its ground, with the frames it is textured by. */
  setWater(water: WaterMap | null, frames: readonly RgbaImage[], nowMs: number): void {
    this.water = null;
    if (water === null || this.contextLost) return;
    try {
      this.water = new GlWater(this.gl, water, frames, nowMs);
    } catch (error) {
      console.warn(`water unavailable (${String(error)}); lakes are left undrawn`);
    }
  }

  /** The level a water surface sits at under a point, or null: `GetWaterHeight`. */
  waterHeightAt(xMm: number, yMm: number): number | null {
    return this.water?.heightAt(xMm, yMm) ?? null;
  }

  weaponAttackClip(vnum: number): ClipName {
    return this.painter.weaponAttackClip(vnum);
  }

  activeClip(entityId: number): ClipName | null {
    return this.painter.activeClip(entityId);
  }

  activeClipEntry(entityId: number): ClipEntry | null {
    return this.painter.activeClipEntry(entityId);
  }

  attackClipEntry(armourVnum: number, race: number, clip: ClipName): ClipEntry | null {
    return this.painter.attackClipEntry(armourVnum, race, clip);
  }

  defendingSpheres(entityId: number): readonly PlacedSphere[] {
    return this.painter.defendingSpheres(entityId);
  }

  attackClipDurationMs(armourVnum: number, race: number, clip: ClipName): number {
    return this.painter.attackClipDurationMs(armourVnum, race, clip);
  }

  attackHitStartMs(armourVnum: number, race: number, clip: ClipName): number | null {
    return this.painter.attackHitStartMs(armourVnum, race, clip);
  }

  skillClipHoldMs(armourVnum: number, race: number, clip: ClipName, mountedWeapon: number | null = null): number | null {
    return this.painter.skillClipHoldMs(armourVnum, race, clip, mountedWeapon);
  }

  attackIsRanged(weaponVnum: number): boolean {
    return this.painter.attackIsRanged(weaponVnum);
  }

  comboChainGapMsFor(armourVnum: number, race: number, clip: ClipName): number | null {
    return this.painter.comboChainGapMsFor(armourVnum, race, clip);
  }

  comboChainFor(race: number, clip: ClipName, comboType: number): readonly ClipName[] | null {
    return this.painter.comboChainFor(race, clip, comboType);
  }

  comboSwingIntervalMsFor(
    armourVnum: number, race: number, clip: ClipName, attackSpeed: number,
  ): number | null {
    return this.painter.comboSwingIntervalMsFor(armourVnum, race, clip, attackSpeed);
  }

  /** The particle layer. See `SceneRenderer.effects`. */
  get effects(): EffectRenderer {
    return this.fallbackRenderer?.effects ?? this.painter.effects;
  }

  /** See `Renderer.renderedCharacterParts`: this stays valid across a WebGL
   * context-loss handoff, where the fallback owns the current visible frame. */
  renderedCharacterParts(entityId: number): RenderedCharacterParts | null {
    return this.fallbackRenderer?.renderedCharacterParts(entityId)
      ?? this.painter.renderedCharacterParts(entityId);
  }

  /** See `SceneRenderer.weaponAnchor`: same handoff as `renderedCharacterParts`. */
  weaponAnchor(entityId: number): { xMm: number; yMm: number; zMm: number; headingRad: number } | null {
    return this.fallbackRenderer?.weaponAnchor(entityId) ?? this.painter.weaponAnchor(entityId);
  }

  /** See `SceneRenderer.weaponAnchorLeft`: same handoff again. */
  weaponAnchorLeft(entityId: number): { xMm: number; yMm: number; zMm: number; headingRad: number } | null {
    return this.fallbackRenderer?.weaponAnchorLeft(entityId) ?? this.painter.weaponAnchorLeft(entityId);
  }

  /** See `SceneRenderer.headAnchor`: same handoff again. */
  headAnchor(entityId: number, mountVnum = 0): { xMm: number; yMm: number; zMm: number } | null {
    return this.fallbackRenderer?.headAnchor(entityId, mountVnum)
      ?? this.painter.headAnchor(entityId, mountVnum);
  }

  /** See `Renderer.markRect`: same context-loss handoff as
   *  `renderedCharacterParts`. */
  markRect(entityId: number): { x: number; y: number; width: number; height: number } | null {
    return this.fallbackRenderer?.markRect(entityId) ?? this.painter.markRect(entityId);
  }

  setPixelRatioCap(cap: number): void {
    this.pixelRatioCap = Number.isFinite(cap)
      ? Math.max(1, Math.min(MAX_DEVICE_PIXEL_RATIO, cap))
      : MAX_DEVICE_PIXEL_RATIO;
    if (this.contextLost) this.fallbackRenderer?.setPixelRatioCap(this.pixelRatioCap);
    else this.resize();
  }

  setNight(night: boolean): void {
    this.night = night;
    this.atmosphere.setNight(night);
    // The night record is a different sky and a different floor, not just a
    // different screen gain. Truthiness rather than `!== null`, because a
    // renderer that never ran its constructor -- the context-loss tests build
    // one with `Object.create` -- has no record and nothing to re-light.
    if (this.environment) this.applyActiveEnvironment();
    this.fallbackRenderer?.setNight(night);
  }

  setEnvironment(environment: MapEnvironment, night: MapEnvironment | null): void {
    this.environment = environment;
    this.atmosphere.setEnvironment(environment, night);
    this.applyActiveEnvironment();
  }

  private applyActiveEnvironment(): void {
    const active = this.atmosphere.active;
    let settled = (): void => {};
    this.environmentReady = new Promise<void>((resolve) => { settled = resolve; });
    this.terrain.setSlopeFloor(slopeFloorFor(active));
    setCreatureLight(active);
    this.flare?.setEnvironment(active);
    const sky = this.sky;
    if (sky === null) {
      // Nothing left to wait for. Leaving the promise pending here would hang
      // every caller that awaits it on a machine whose driver refused the sky
      // shaders.
      settled();
      return;
    }
    sky.setEnvironment(active, null);
    void loadCloudTexture(active).then((cloud) => {
      if (cloud !== null && this.atmosphere.active === active) sky.setEnvironment(active, cloud);
      settled();
    });
  }

  /** This frame's fade -- where it starts, where it ends, and what it ends in.
   *  One place, so the ground, the props and the cleared buffer cannot be given
   *  three answers on one frame. */
  private get fade(): { nearMm: number; farMm: number; colour: readonly [number, number, number] } {
    if (this.environment === null) {
      return { nearMm: FOG_NEAR_MM, farMm: FOG_FAR_MM, colour: VOID_COLOUR };
    }
    return fogFor(this.atmosphere.active);
  }

  setWeather(kind: number): void {
    this.weatherKind = kind;
    // Once the shared context is lost, the fallback owns the whole picture.
    if (this.contextLost) {
      this.fallbackRenderer?.setWeather(kind);
      return;
    }
    this.snowing = kind === 1;
    if (this.snowing) this.loadSnowTexture();
    this.fallbackRenderer?.setWeather(kind);
  }

  resize(): void {
    this.dpr = Math.min(this.pixelRatioCap, globalThis.devicePixelRatio ?? 1);
    const width = this.overlayCanvas.clientWidth || 960;
    const height = this.overlayCanvas.clientHeight || 540;
    const pixelWidth = Math.round(width * this.dpr);
    const pixelHeight = Math.round(height * this.dpr);

    if (this.contextLost) {
      // `drawFallback` copies this camera into the Canvas2D renderer every
      // frame. Keep its CSS-pixel viewport current as well as resizing the
      // fallback canvas; otherwise a Safari window resize after context loss
      // is immediately overwritten with the old projection dimensions.
      this.camera.resize(width, height);
      this.fallbackRenderer?.resize?.();
      return;
    }

    this.overlayCanvas.width = pixelWidth;
    this.overlayCanvas.height = pixelHeight;
    this.glCanvas.width = pixelWidth;
    this.glCanvas.height = pixelHeight;
    this.camera.resize(width, height);
  }

  /** See `render/drawgate.ts`. Off by default; only a check ever turns it on. */
  setReducedDraw(on: boolean): void {
    this.reduced = on;
  }

  draw(frame: RenderFrame): void {
    if (this.contextLost) {
      // Safari can change devicePixelRatio while moving a window between
      // displays without a resize event. The Canvas2D handoff remains the
      // visible renderer after a loss, so it needs the same per-frame repair
      // as the healthy GL path before its camera is copied into the fallback.
      this.resizeIfNeeded();
      this.drawFallback(frame);
      return;
    }
    // Safari can change devicePixelRatio when a window moves between displays
    // without dispatching a resize event. Keep the backing buffers and camera
    // viewport in step before projecting this frame.
    this.resizeIfNeeded();
    const cameraTarget = frame.cameraAnchor ?? frame.player;
    this.camera.follow(cameraTarget.xMm, cameraTarget.yMm, frame.dtMs);
    // The ground under wherever the camera ends up looking, not under wherever
    // it started: this is what keeps the eye rising onto a hill with the player
    // rather than levelling out at sea level under their feet, and `update()`
    // has to see it before `viewProj`/`visibleBounds` are read below.
    this.camera.zMm = this.heightField.at(this.camera.xMm, this.camera.yMm);
    this.camera.wave = this.painter.screenWaveOffset(frame.nowMs);
    this.camera.update();
    // Then again, against the ground under the eye this pitch just put there:
    // `ProcessTerrainCollision` runs on the matrices `RotateEyeAroundTarget`
    // has already written and calls `SetEye`, which rebuilds them
    // (`EterLib/Camera.cpp:305-322`, `UserInterface/CameraProcedure.cpp:24`).
    this.camera.avoidGround(this.heightField);
    this.camera.update();

    const gl = this.gl;
    const clock = this.profile;
    // The drawing buffer can be resized out from under us by a device-pixel-ratio
    // change with no resize event, so the viewport is set from the buffer rather
    // than from whatever `resize` last computed.
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    // The same colour the fade runs to, from the same record: geometry that
    // reaches the background before the clip cuts it is what makes the clip
    // invisible, and two constants for one fact is how they drift apart.
    const void_ = this.fade.colour;
    gl.clearColor(void_[0], void_[1], void_[2], 1);
    // The depth buffer too: a heightfield's own displacement means yesterday's
    // ridge is not this frame's, and clearing only the colour would let last
    // frame's depths reject this frame's nearer ground.
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    if (this.reduced) {
      this.drawActors(frame);
      return;
    }

    this.sky?.draw(
      { viewProj: this.camera.viewProj, eyeMm: this.camera.eyeMm },
      frame.nowMs / 1000,
    );
    // `RenderSky`, then `RenderBeforeLensFlare`, then the world
    // (`PythonApplication.cpp:189-215`): the sun's own disc writes no depth, so
    // the terrain and the buildings drawn after it cover it.
    const flareView = this.flareView();
    this.flare?.drawBeforeFlare(flareView);

    const passStart = now();
    const bounds = this.camera.visibleBounds(0, {
      minMm: this.heightField.texture.minMm,
      maxMm: this.heightField.texture.minMm + this.heightField.texture.spanMm,
    });

    const fog = this.camera.fog(this.fade.colour, this.fade.nearMm, this.fade.farMm);

    const terrainStart = now();
    this.terrain.draw({ viewProj: this.camera.viewProj }, bounds, frame.nowMs / 1000, fog, this.characterShadow);
    clock.terrainMs += now() - terrainStart;
    clock.setupMs += terrainStart - passStart;

    const view = { viewProj: this.camera.viewProj };
    const blocker = this.pcBlockerFrame();
    const propsStart = now();
    this.buildings?.draw(view, bounds, this.camera.eyeMm, fog, blocker, {
      xMm: this.camera.xMm,
      yMm: this.camera.yMm,
    });
    this.trees?.draw(view, bounds, this.camera.billboardRight, fog, blocker);
    clock.propsMs += now() - propsStart;

    const atmosphereStart = now();
    this.atmosphere.draw(gl.drawingBufferWidth, gl.drawingBufferHeight);
    // `RenderAfterLensFlare` is the last thing the frame draws
    // (`PythonApplication.cpp:215`).
    this.flare?.drawAfterFlare(flareView);
    clock.atmosphereMs += now() - atmosphereStart;

    this.refreshBuildingSurfaces(frame);
    this.drawDropItems(view, fog, frame);
    this.drawGuildSymbols(view, fog, frame);
    this.drawActors(frame);
    // `RenderWater` follows the characters (`PythonApplication.cpp:196-201`).
    this.water?.draw(view, bounds, fog, frame.nowMs);
    // `RenderSnow` follows the water (`PythonApplication.cpp:201-202`).
    this.drawSnow(view, frame.nowMs);
    // `RenderEffect`, the map's own effects, follows the snow
    // (`PythonApplication.cpp:196-199`).
    this.painter.effects.drawPlaced(this.camera, frame.nowMs, frame.player.xMm, frame.player.yMm);
    // `RenderPCBlocker` is the last world pass, after the characters
    // (`UserInterface/PythonApplication.cpp:198,212`).
    if (blocker !== null) {
      this.buildings?.drawBlockers(fog, blocker);
      this.trees?.drawBlockers(fog, blocker, this.camera.billboardRight);
    }
  }

  setGuildAttributes(table: GuildAttributeTable): void {
    this.guildAttributes = table;
    this.buildingSurfaceKey = "";
  }

  /** `CMapOutdoor::GetHeight` answers a standing guild building's own surface
   *  (`ActorInstance.cpp:524-533`, `MapOutdoor.cpp:15-35`): the height field's
   *  building surfaces follow the buildings in view. */
  private refreshBuildingSurfaces(frame: RenderFrame): void {
    if (this.guildAttributes === null) return;
    const standing = frame.others
      .filter((other) => other.kind === EntityKind.BUILDING && this.guildAttributes!.buildings[String(other.buildingVnum)] !== undefined)
      .map((other) => ({ vnum: other.buildingVnum, xMm: other.xMm, yMm: other.yMm, heading: other.heading }));
    const key = standing.map((b) => `${b.vnum}@${b.xMm},${b.yMm},${b.heading}`).join(";");
    if (key === this.buildingSurfaceKey) return;
    this.buildingSurfaceKey = key;
    this.heightField.setBuildingSurface(standing.length === 0 ? null : ObjectSurface.fromBuildings(
      this.guildAttributes,
      standing.map((b) => ({ ...b, zMm: this.heightField.terrainAt(b.xMm, b.yMm) })),
    ));
  }

  /** The map's tree collision objects, which the foliage half of the blocker
   *  sweep takes a tree on (`MapOutdoorUpdate.cpp:545-554`). */
  setTreeCollision(data: TreeCollisionData): void {
    this.trees?.setShapes(data);
  }

  /** Every guild insignia in view in its own model, wearing its guild's symbol
   *  once one is served (`InstanceBase.cpp:832-839`); the painter is told which
   *  ones need no schematic. */
  private drawGuildSymbols(view: { viewProj: Mat4 }, fog: Fog, frame: RenderFrame): void {
    const drawn = new Set<number>();
    if (this.guildSymbols !== null) {
      const placed: PlacedInsignia[] = [];
      for (const other of frame.others) {
        if (other.kind !== EntityKind.BUILDING || other.buildingVnum !== GUILD_SYMBOL_RACE) continue;
        placed.push({
          xMm: other.xMm, yMm: other.yMm, zMm: this.heightField.at(other.xMm, other.yMm),
          heading: other.heading, guildId: other.guildId,
        });
        drawn.add(other.id);
      }
      this.guildSymbols.draw(view, fog, placed, (guildId) => this.symbolTexture(guildId, frame.guildSymbols));
    }
    this.painter.setInsigniaModelIds(drawn);
  }

  /** A guild's served symbol as a texture, uploaded again only when the
   *  session holds a different image for that guild. */
  private symbolTexture(guildId: number, served: ReadonlyMap<number, CanvasImageSource> | undefined): WebGLTexture | null {
    const image = served?.get(guildId);
    if (image === undefined) return null;
    const cached = this.symbolTextures.get(guildId);
    if (cached !== undefined && cached.image === image) return cached.texture;
    if (cached !== undefined) this.gl.deleteTexture(cached.texture);
    const texture = createPropTexture(this.gl, image as ImageBitmap);
    this.symbolTextures.set(guildId, { image, texture });
    return texture;
  }

  /** `CPythonItem::Render` (`PythonItem.cpp:121-132`): every ground item this
   *  client was told the vnum of, in that vnum's drop model on the ground under
   *  it; the painter is told which ones need no glyph. */
  private drawDropItems(view: { viewProj: Mat4 }, fog: Fog, frame: RenderFrame): void {
    const drawn = new Set<number>();
    const told = frame.groundItems;
    if (this.dropItems !== null && told !== undefined) {
      const lying: LyingItem[] = [];
      for (const other of frame.others) {
        if (other.kind !== EntityKind.ITEM) continue;
        const seen = told.get(other.id);
        if (seen === undefined || this.dropItems.modelFor(seen.vnum) === null) continue;
        lying.push({
          id: other.id, vnum: seen.vnum, xMm: other.xMm, yMm: other.yMm,
          zMm: this.heightField.at(other.xMm, other.yMm), seenMs: seen.seenMs,
        });
        drawn.add(other.id);
      }
      this.dropItems.draw(view, fog, lying, frame.nowMs);
    }
    this.painter.setDropModelIds(drawn);
  }

  /** `Deform` then `Render`: the field moves around the camera's target and
   *  is drawn into the depth-tested scene (`PythonBackground.cpp:342,461,471`). */
  private drawSnow(view: { viewProj: Mat4 }, nowMs: number): void {
    const start = now();
    const elapsedS = this.lastSnowMs === null ? 0 : Math.max(0, nowMs - this.lastSnowMs) / 1000;
    this.lastSnowMs = nowMs;
    const m = this.camera.viewMatrix;
    const basis: SnowBasis = {
      view: [-m[2], -m[6], -m[10]],
      up: [m[1], m[5], m[9]],
      cross: [m[0], m[4], m[8]],
    };
    this.snowField.update(elapsedS, [this.camera.xMm, this.camera.yMm, this.camera.zMm], basis, this.snowing);
    this.snowPass?.draw(view, this.snowField, basis);
    this.profile.snowMs += now() - start;
  }

  /** `d:/ymir work/special/snow.dds` is the flake (`SnowEnvironment.cpp:290`);
   *  fetched once, the first time it snows. */
  private loadSnowTexture(): void {
    if (this.snowPass !== null || this.snowTexturePending) return;
    this.snowTexturePending = true;
    loadImage("/weather/snow.png")
      .then((image) => {
        if (this.contextLost) return;
        this.snowPass = new GlSnow(this.gl, createPropTexture(this.gl, image));
      })
      .catch((error: unknown) => {
        this.snowTexturePending = false;
        console.warn(`snow unavailable (${String(error)})`);
      });
  }

  /** The three points and the two matrices the blocker sweep and its ramp
   *  need, or `null` when there is no viewport to index the ramp in. */
  private pcBlockerFrame(): PcBlockerFrame | null {
    const widthPx = this.gl.drawingBufferWidth;
    const heightPx = this.gl.drawingBufferHeight;
    if (widthPx <= 0 || heightPx <= 0) return null;
    return {
      ...this.camera.pcBlockerFrame,
      view: { viewProj: this.camera.viewProj, view: this.camera.viewMatrix },
      widthPx,
      heightPx,
    };
  }

  /** What `CLensFlare` is handed each frame: the camera's target and eye, and
   *  the viewport it projects into (`LensFlare.cpp:127-141`, `:169-170`). */
  private flareView(): FlareView {
    const eye = this.camera.eyeMm;
    return {
      viewProj: this.camera.viewProj,
      targetMm: [this.camera.xMm, this.camera.yMm, this.camera.zMm],
      eyeMm: [eye.xMm, eye.yMm, eye.zMm],
      widthPx: this.gl.drawingBufferWidth,
      heightPx: this.gl.drawingBufferHeight,
    };
  }

  private drawActors(frame: RenderFrame): void {
    const clock = this.profile;
    const actorsStart = now();
    const ctx = this.overlay;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    // Cleared, not filled: everything under the entities is on the GL canvas, and
    // an opaque fill here would hide it.
    ctx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    // `RenderCharacterShadowToTexture`, aimed at the camera's target
    // (`PythonBackground.cpp:492-526`); the next frame's terrain reads it.
    this.characterShadow?.begin([this.camera.xMm, this.camera.yMm, this.camera.zMm]);
    this.painter.draw(frame);
    this.characterShadow?.end();
    ctx.restore();
    clock.actorsMs += now() - actorsStart;
    clock.frames += 1;
  }

  drawJoystick(
    origin: { x: number; y: number },
    knob: { x: number; y: number },
    radiusPx: number,
  ): void {
    if (this.contextLost) {
      this.fallbackRenderer?.drawJoystick(origin, knob, radiusPx);
      return;
    }
    const ctx = this.overlay;
    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.painter.drawJoystick(origin, knob, radiusPx);
    ctx.restore();
  }

  private activateFallback(): void {
    if (this.fallbackRenderer !== null) return;
    // Safari may keep the lost framebuffer's last pixels composited until the
    // next animation frame. Hide that dead layer immediately so the player
    // cannot see a frozen GL scene during the handoff; retain the canvas itself
    // because removing it would discard the browser's context lifecycle target.
    this.glCanvas.style.visibility = "hidden";
    this.glCanvas.style.pointerEvents = "none";
    // The GL renderer's page canvas was opened with alpha:true and therefore
    // cannot be safely reused as the opaque Canvas2D renderer's target. Give
    // the fallback its own opaque canvas in the same DOM slot; it covers the
    // lost GL layer while leaving the input-bound overlay and HUD untouched.
    const canvas = document.createElement("canvas");
    stack(canvas);
    this.overlayCanvas.parentNode?.insertBefore(canvas, this.overlayCanvas);
    const fallback = new Renderer(canvas, this.heightField);
    fallback.setPixelRatioCap(this.pixelRatioCap);
    fallback.setPalettes(this.palettes);
    fallback.setNight(this.night);
    fallback.setWeather(this.weatherKind);
    if (this.terrainMap !== null) fallback.setTerrain(this.terrainMap);
    this.fallbackRenderer = fallback;
  }

  private resizeIfNeeded(): void {
    const dpr = Math.min(this.pixelRatioCap, globalThis.devicePixelRatio ?? 1);
    const width = this.overlayCanvas.clientWidth || 960;
    const height = this.overlayCanvas.clientHeight || 540;
    const pixelWidth = Math.round(width * dpr);
    const pixelHeight = Math.round(height * dpr);
    if (
      this.dpr !== dpr ||
      this.overlayCanvas.width !== pixelWidth ||
      this.overlayCanvas.height !== pixelHeight ||
      this.glCanvas.width !== pixelWidth ||
      this.glCanvas.height !== pixelHeight
    ) {
      this.resize();
    }
  }

  /** Keep camera controls continuous across the renderer handoff. */
  private drawFallback(frame: RenderFrame): void {
    const fallback = this.fallbackRenderer;
    if (fallback === null) return;
    // The transparent overlay still contains the last successful GL frame.
    // Clear it so those stale silhouettes do not float over the opaque fallback
    // canvas that now owns the scene.
    const ctx = this.overlay;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
    ctx.restore();
    // Keep the eased target position as well as the presentation controls. A
    // fallback camera is constructed at the origin; letting its first
    // `follow()` start there makes a context loss after movement visibly jump
    // the world before it catches up to the player.
    fallback.camera.xMm = this.camera.xMm;
    fallback.camera.yMm = this.camera.yMm;
    fallback.camera.zMm = this.camera.zMm;
    fallback.camera.yawDeg = this.camera.yawDeg;
    fallback.camera.mmPerPixel = this.camera.mmPerPixel;
    fallback.camera.pitchDeg = this.camera.pitchDeg;
    fallback.camera.viewportWidth = this.camera.viewportWidth;
    fallback.camera.viewportHeight = this.camera.viewportHeight;
    fallback.draw(frame);
    this.camera.xMm = fallback.camera.xMm;
    this.camera.yMm = fallback.camera.yMm;
    this.camera.zMm = fallback.camera.zMm;
    this.camera.yawDeg = fallback.camera.yawDeg;
    this.camera.mmPerPixel = fallback.camera.mmPerPixel;
    this.camera.pitchDeg = fallback.camera.pitchDeg;
    this.camera.viewportWidth = fallback.camera.viewportWidth;
    this.camera.viewportHeight = fallback.camera.viewportHeight;
    this.camera.update();
  }
}

function buildProps(
  gl: WebGL2RenderingContext,
  props: PropAssets,
): { buildings: GlBuildings; trees: GlTrees } | null {
  try {
    const textures = new Map<string, WebGLTexture>();
    for (const [key, image] of props.images) {
      textures.set(key, createPropTexture(gl, image));
    }
    const buildings = new GlBuildings(gl, props.meshBlob, props.meshManifest, textures, props.placements.buildings);
    const trees = new GlTrees(gl, textures, props.placements.trees, props.textureManifest);
    console.log(
      `props: ${props.placements.buildings.length} buildings, ` +
        `${trees.resolvedCount}/${props.placements.trees.length} trees have a texture to draw`,
    );
    return { buildings, trees };
  } catch (error) {
    console.warn(`buildings/trees unavailable (${String(error)}); rendering the terrain alone`);
    return null;
  }
}

function stack(element: HTMLElement): void {
  const style = element.style;
  style.position = "fixed";
  style.left = "0";
  style.top = "0";
  style.width = "100vw";
  style.height = "100vh";
  style.zIndex = "0";
}
